import React, { useEffect, useRef, useState } from 'react';
import { siteData } from '../data/mock';
import { ArrowUpRight, Mail } from 'lucide-react';
import { ContactModal } from './ContactModal';

export const ContactSection = () => {
  const { contact } = siteData;
  const [visible, setVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('');
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleCardClick = (cardTitle) => {
    setModalType(cardTitle);
    setModalOpen(true);
  };

  return (
    <section
      ref={sectionRef}
      id="contact"
      className="oa-section"
      style={{ background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-subtle)' }}
    >
      <div className="relative z-10" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Section Label */}
        <div
          className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ marginBottom: '24px' }}
        >
          <span className="oa-number">{contact.number} // {contact.label}</span>
        </div>

        {/* Headline */}
        <h2
          className={`oa-display-large transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ marginBottom: '24px', whiteSpace: 'nowrap' }}
        >
          {contact.headline}
        </h2>

        {/* Body */}
        <p
          className={`oa-body-medium transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ marginBottom: '80px', maxWidth: '640px' }}
        >
          {contact.body}
        </p>

        {/* CTA Cards */}
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-0"
          style={{ marginBottom: '80px' }}
        >
          {contact.cards.map((card, i) => (
            <div
              key={i}
              className={`group oa-card-hover transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{
                transitionDelay: `${300 + i * 100}ms`,
                padding: '40px 32px',
                border: '1px solid rgba(255,255,255,0.08)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: '280px',
                cursor: 'pointer',
              }}
              onClick={() => handleCardClick(card.title)}
            >
              <div>
                <h3 className="oa-heading-2" style={{ color: 'var(--text-primary)', marginBottom: '16px' }}>
                  {card.title}
                </h3>
                <p className="oa-body-small" style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
                  {card.description}
                </p>
              </div>
              <div
                className="flex items-center gap-2 transition-colors duration-300"
                style={{ color: 'var(--brand-primary)' }}
              >
                <span
                  style={{
                    fontSize: '15px',
                    fontWeight: 500,
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  {card.cta}
                </span>
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </div>
            </div>
          ))}
        </div>

        {/* Direct Contacts */}
        <div
          className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionDelay: '600ms' }}
        >
          <span className="oa-number" style={{ display: 'block', marginBottom: '28px' }}>Direct Contact</span>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0">
            {contact.directContacts.map((person, i) => (
              <div
                key={i}
                style={{
                  padding: '24px 24px 24px 0',
                  borderRight: i < 3 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                  paddingLeft: i > 0 ? '24px' : '0',
                }}
              >
                <h4
                  className="oa-heading-3"
                  style={{ color: 'var(--text-primary)', marginBottom: '4px', fontSize: '16px' }}
                >
                  {person.name}
                </h4>
                <span
                  className="oa-body-muted"
                  style={{ fontSize: '13px', display: 'block', marginBottom: '12px' }}
                >
                  {person.area}
                </span>
                <a
                  href={`mailto:${person.email}`}
                  className="flex items-center gap-2 hover:text-[var(--brand-primary)] transition-colors duration-300"
                  style={{
                    fontSize: '13px',
                    color: 'var(--text-secondary)',
                    textDecoration: 'none',
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  <Mail size={12} style={{ opacity: 0.6 }} />
                  {person.email}
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contact Modal */}
      <ContactModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        inquiryType={modalType}
      />
    </section>
  );
};
